import './index.scss';

import { Component, ReactNode, createRef } from 'react';

import { TabsConsumer, TabsProvider } from './context';
import { BraxTabProps } from 'components/BraxTab';
import { BraxNavbar, BraxNavbarItem } from 'components/BraxNavbar';

export type BraxTabsProps<T extends string = string> = {
    initial?: T,
    active?: T,
    prerender: boolean,
    unmount: boolean,
    hidden: boolean,
    className: boolean,
    children: ReactNode,
    onTabChange?: (active: T) => void
};

type BraxTabsState<T extends string = string> = {
    active?: T,
    animating: T | null
};

export class BraxTabs<T extends string = string> extends Component<BraxTabsProps<T>, BraxTabsState<T>> {

    static defaultProps = {
        prerender: false,
        unmount: false,
        hidden: true,
        className: true
    };

    state: BraxTabsState<T> = {
        active: this.props.active || this.props.initial,
        animating: null
    };

    navbarRef = createRef<BraxNavbar<T>>();

    timeout?: number;

    static getDerivedStateFromProps<T extends string = string>(nextProps: BraxTabsProps<T>, prevState: BraxTabsState<T>): BraxTabsState<T> | null {
        if (nextProps.active !== undefined && nextProps.active !== prevState.active)
            return { active: nextProps.active, animating: prevState.active || null };

        return null;
    }

    componentDidUpdate(prevProps: BraxTabsProps<T>) {
        if (prevProps.active !== this.props.active)
            this.handleAnimation();
    }

    change = (active: T) => {
        if (this.state.active !== active) {
            this.setState(prevState => ({ animating: prevState.active || null, active }));

            this.props.onTabChange?.(active);

            this.handleAnimation();
        }
    }

    handleAnimation() {
        if (this.timeout !== undefined)
            window.clearTimeout(this.timeout);

        this.timeout = window.setTimeout(() => this.setState({ animating: null }), 500);
    }

    checkSize() {
        this.navbarRef.current?.checkSize();
    }

    _memoizedNavbarItens: { arg: BraxTabProps<T>[], value: BraxNavbarItem<T>[] } | null = null;
    getMemoizedNavbarItens(tabs: BraxTabProps<T>[]) {
        if (this._memoizedNavbarItens === null || this._memoizedNavbarItens.arg !== tabs)
            this._memoizedNavbarItens = {
                arg: tabs,
                value: tabs.map(tab => ({
                    key: tab.id,
                    content: tab.title,
                    disabled: tab.disabled
                }))
            };

        return this._memoizedNavbarItens.value;
    }

    render() {
        const { active, initial, children, onTabChange, ...props } = this.props;

        return (
            <TabsProvider<T> active={this.state.active} animating={this.state.animating} {...props}>
                <TabsConsumer<T>>
                    { context => {
                        const index = context.tabs.findIndex(tab => tab.id === context.active);

                        return (
                            <div className={`brax-tabs ${context.active ? `tab-active-${context.active}` : ''}`}>
                                <BraxNavbar<T>
                                    ref={this.navbarRef}
                                    active={context.active}
                                    itens={this.getMemoizedNavbarItens(context.tabs)}
                                    onChange={this.change}
                                />

                                <div>
                                    <div style={{ '--current-index': index !== -1 ? index : 0 } as any}>
                                        { this.props.children }
                                    </div>
                                </div>
                            </div>
                        );
                    } }
                </TabsConsumer>
            </TabsProvider>
        );
    }

}
